export default function CodeTycoonPrivacy() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Privacy Policy - Idle Coding Tycoon
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Effective date: <time dateTime="2026-05-31">May 31, 2026</time>
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            <strong>Cosmovex</strong> built the{" "}
            <strong>Idle Coding Tycoon</strong> game as a free,
            ad-supported application. This Privacy Policy explains what
            information the game may process, how that information is used,
            and the choices you have when playing.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Information We Process
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Game progress:</strong> your developers, merges,
              upgrades, Lines of Code, Funding, prestige multipliers, and
              other game state are stored locally on your device only. This
              progress is never uploaded to our servers. There are no
              accounts, no sign-in, and no cloud save.
            </li>
            <li>
              <strong>Offline earnings:</strong> while the game is closed,
              earnings are calculated on your device using the time elapsed.
              No data is sent anywhere to do this.
            </li>
            <li>
              <strong>Game preferences:</strong> theme choice, sound
              settings, and language are stored locally on your device.
            </li>
            <li>
              <strong>Usage and ad data:</strong> if ads are enabled, AdMob
              or related Google Mobile Ads services may collect device and
              advertising data, including an advertising identifier, as
              described in Google&apos;s own privacy documentation.
            </li>
            <li>
              <strong>Analytics and diagnostics:</strong> Firebase Analytics
              and Firebase Crashlytics may collect app stability, usage
              patterns, and crash reports to improve reliability and the
              player experience.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            How We Use Information
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              To run the game: hiring developers, merging tiles, generating
              Lines of Code, and processing upgrades and prestige.
            </li>
            <li>
              To calculate offline earnings while the game is closed.
            </li>
            <li>
              To remember your settings, preferences, and saved progress.
            </li>
            <li>
              To improve game performance, reliability, and product quality.
            </li>
            <li>
              To display ads in supported versions of the game, where
              applicable.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Information We Do Not Collect
          </h2>
          <p>
            Idle Coding Tycoon does not collect your name, email address,
            contacts, precise or approximate location, files, or photos. The
            game works fully offline and does not require an account.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Local Storage</h2>
          <p>
            Idle Coding Tycoon is designed to keep your data on your device.
            Your save, settings, and preferences are stored locally and are
            not uploaded to any server. Because progress is local-only, it
            cannot be transferred between devices and is removed if you clear
            the app&apos;s storage or uninstall the game.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Network Access
          </h2>
          <p>
            Playing the game does not require network access — all game
            logic runs on your device. The game makes network requests only
            for ads, Firebase analytics and crash reporting, Firebase Remote
            Config, and a small public list of other Cosmovex apps used to
            show a cross-promotion card. No game progress or save data is
            ever transmitted.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Advertising</h2>
          <p>
            Idle Coding Tycoon may use <strong>Google AdMob</strong> to show
            banner, interstitial, and rewarded ad formats. Rewarded videos
            are optional and grant temporary in-game boosts. AdMob may
            collect and process identifiers, device information, approximate
            location, and ad interaction data to deliver and measure ads. You
            can remove ads via a one-time Pro upgrade.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            In-App Purchases
          </h2>
          <p>
            Idle Coding Tycoon offers an optional one-time Pro upgrade that
            removes advertisements. Purchases are processed through Google
            Play Billing and are subject to Google&apos;s terms and privacy
            policies. We do not receive or store your payment details.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Third-Party Services
          </h2>
          <p>Idle Coding Tycoon may rely on third-party services such as:</p>
          <ul className="list-disc space-y-2 pl-6">
            <li>Google Play Services</li>
            <li>Google Play Billing</li>
            <li>Google AdMob</li>
            <li>Firebase Analytics</li>
            <li>Firebase Crashlytics</li>
            <li>Firebase Remote Config</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Sharing</h2>
          <p>
            We do not sell your game progress or personal data. Data may be
            shared only with service providers required to operate game
            features, such as advertising, analytics, app distribution, or
            diagnostics infrastructure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Retention</h2>
          <p>
            Local game data remains on your device until you clear the
            app&apos;s storage or uninstall the game. Data collected by
            third-party services such as AdMob and Firebase is retained
            according to those providers&apos; own policies.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Children&apos;s Privacy
          </h2>
          <p>
            Idle Coding Tycoon is intended for a general audience aged 13 and
            over. It is not directed at children, and we do not knowingly
            collect personal information from children through the game.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Security</h2>
          <p>
            We take reasonable steps to protect data processed by the game,
            but no method of storage or transmission is completely secure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Your Choices</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              You can uninstall the game to remove all locally stored data
              from your device.
            </li>
            <li>
              You can purchase the Pro upgrade to disable all advertising.
            </li>
            <li>
              You can manage ad personalization and advertising controls
              through your device and Google settings.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. When we do,
            we will post the updated version on this page and revise the
            effective date.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact Us</h2>
          <p>
            For privacy-related questions about Idle Coding Tycoon, contact{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            .
          </p>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
